import JsBarcode from "jsbarcode";

export const DEFAULT_RIBBON_WIDTH_MM = 29;
const PADDING_MM = 1.5;
const FALLBACK_ASPECT_RATIO = 2.6;

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

interface RenderedBarcode {
  markup: string;
  aspectRatio: number;
}

const renderBarcodeSvg = (value: string): RenderedBarcode => {
  const container = document.createElement("div");
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  container.appendChild(svg);

  JsBarcode(svg, value, {
    format: "CODE128",
    displayValue: true,
    height: 80,
    fontSize: 16,
    margin: 0,
    marginBottom: 6,
    textMargin: 2,
    background: "#ffffff",
    lineColor: "#000000"
  });

  const intrinsicWidth =
    parseFloat(svg.getAttribute("width") ?? "0") || 0;
  const intrinsicHeight =
    parseFloat(svg.getAttribute("height") ?? "0") || 0;
  const aspectRatio =
    intrinsicWidth > 0 && intrinsicHeight > 0
      ? intrinsicWidth / intrinsicHeight
      : FALLBACK_ASPECT_RATIO;

  // Drop the px width/height so CSS controls the rendered size — keep viewBox
  // so preserveAspectRatio can scale the bars without distortion.
  svg.removeAttribute("width");
  svg.removeAttribute("height");
  svg.setAttribute("preserveAspectRatio", "xMidYMid meet");

  return { markup: container.innerHTML, aspectRatio };
};

interface PrintBarcodeOptions {
  ribbonWidthMm?: number;
}

export const printBarcodeLabel = (
  value: string,
  options: PrintBarcodeOptions = {}
): void => {
  if (!value) return;

  const ribbonWidthMm =
    Number.isFinite(options.ribbonWidthMm) &&
    (options.ribbonWidthMm as number) > 0
      ? (options.ribbonWidthMm as number)
      : DEFAULT_RIBBON_WIDTH_MM;

  let rendered: RenderedBarcode;
  try {
    rendered = renderBarcodeSvg(value);
  } catch {
    return;
  }

  // Reserve PADDING_MM on each side; the barcode fills the inner box.
  const innerWidthMm = Math.max(ribbonWidthMm - PADDING_MM * 2, 1);
  const innerHeightMm = innerWidthMm / rendered.aspectRatio;
  const pageHeightMm = innerHeightMm + PADDING_MM * 2;

  const printWindow = window.open("", "_blank", "width=420,height=320");
  if (!printWindow) return;

  const safeValue = escapeHtml(value);

  printWindow.document.open();
  printWindow.document.write(`<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>Barcode ${safeValue}</title>
    <style>
      @page {
        size: ${ribbonWidthMm}mm ${pageHeightMm.toFixed(2)}mm;
        margin: 0;
      }
      html,
      body {
        margin: 0;
        padding: 0;
        background: #fff;
      }
      body {
        width: ${ribbonWidthMm}mm;
        height: ${pageHeightMm.toFixed(2)}mm;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .label {
        width: 100%;
        height: 100%;
        padding: ${PADDING_MM}mm;
        box-sizing: border-box;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .label svg {
        display: block;
        width: 100%;
        height: 100%;
      }
    </style>
  </head>
  <body>
    <div class="label">${rendered.markup}</div>
    <script>
      window.addEventListener("load", function () {
        window.focus();
        window.print();
        window.addEventListener("afterprint", function () {
          window.close();
        });
      });
    </script>
  </body>
</html>`);
  printWindow.document.close();
};
